import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Eye, Download, AlertCircle, ExternalLink, RefreshCw, ArrowUp, Edit2, Check } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { userApi } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ImageUpload = ({ 
  type, 
  files = [], 
  onFilesChange, 
  multiple = true,
  maxSizeMB = 10,  // Increased to 10MB
  title,
  description,
  infoLink,
  displayNames = []  // Array of display names corresponding to files
}) => {
  const { user, refreshUser } = useAuth();
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [compressionProgress, setCompressionProgress] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState(new Set());
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingName, setEditingName] = useState('');

  // Handle ESC key for lightbox
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && lightboxOpen) {
        setLightboxOpen(false);
      }
    };

    if (lightboxOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [lightboxOpen]);

  const compressImage = async (file, fileIndex) => {
    const options = {
      maxSizeMB: 1.5, // Compress to reasonable size
      maxWidthOrHeight: 1200, // Max 1200px width/height as requested
      useWebWorker: true,
      fileType: 'image/jpeg', // Convert to JPG to save space
      initialQuality: 0.75, // 75% quality for good balance
      onProgress: (progress) => {
        setCompressionProgress(prev => ({
          ...prev,
          [fileIndex]: Math.round(progress)
        }));
      }
    };

    try {
      const compressedFile = await imageCompression(file, options);
      
      // CRITICAL FIX: Preserve original filename
      // browser-image-compression returns a file with name "blob" - we need to restore original name
      const originalName = file.name;
      const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
      const renamedFile = new File([compressedFile], `${nameWithoutExt}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      
      // Clear compression progress for this file
      setCompressionProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[fileIndex];
        return newProgress;
      });
      
      return renamedFile;
    } catch (error) {
      console.error('Compression failed:', error);
      return file; // Return original if compression fails
    }
  };

  const createThumbnail = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = async (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0 || !user) return;
    
    setUploading(true);
    const newFiles = [];
    
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const tempId = Date.now() + i;
        
        // Validate file type
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
          alert(`Fil "${file.name}" har ugyldig format. Kun PDF, JPG og PNG er tillatt.`);
          continue;
        }
        
        // PDF size validation (1MB max)
        if (file.type === 'application/pdf' && file.size > 1 * 1024 * 1024) {
          toast.error(`${file.name} er for stor. PDF-filer kan maksimalt være 1MB. Komprimer filen og prøv igjen.`, 6000);
          continue;
        }
        
        // General file size validation
        if (file.type !== 'application/pdf' && file.size > maxSizeMB * 1024 * 1024) {
          const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
          toast.error(
            `${file.name} er for stor (${fileSizeMB}MB). Maksimalt ${maxSizeMB}MB tillatt. ` +
            `Tips: Reduser bildestørrelsen på telefonen eller komprimer bildet før opplasting.`,
            8000
          );
          continue;
        }
        
        // Track this file as uploading
        setUploadingFiles(prev => new Set(prev).add(tempId));
        
        let processedFile = file;
        
        // Compress ALL images for optimal storage and cost savings
        if (file.type.startsWith('image/')) {
          console.log(`🗜️ Compressing ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB) to 1200px max...`);
          processedFile = await compressImage(file, i);
          console.log(`✅ Compressed to ${(processedFile.size / 1024 / 1024).toFixed(2)}MB (-${Math.round((1 - processedFile.size/file.size) * 100)}%)`);
        }
        
        try {
          // Upload to backend
          const response = await userApi.uploadDocument(user.id, processedFile, type);
          
          // Create file object with backend URL
          const fileObject = {
            id: tempId,
            name: processedFile.name,
            originalName: file.name,
            size: processedFile.size,
            originalSize: file.size,
            type: processedFile.type,
            uploadDate: new Date().toISOString(),
            // Check if file_url is already a full URL (e.g., CloudFront) or needs domain prefix
            url: response.file_url.startsWith('http') ? response.file_url : `${window.location.origin}${response.file_url}`,
            serverUrl: response.file_url, // Store server path for deletion
            compressed: processedFile.size < file.size,
            uploaded: true
          };
          
          newFiles.push(fileObject);
          
        } catch (uploadError) {
          console.error(`Upload failed for ${file.name}:`, uploadError);
          alert(`Kunne ikke laste opp ${file.name}. ${uploadError.response?.data?.detail || 'Ukjent feil'}`);
        } finally {
          // Remove from uploading set
          setUploadingFiles(prev => {
            const newSet = new Set(prev);
            newSet.delete(tempId);
            return newSet;
          });
        }
      }
      
      // Add successfully uploaded files to existing files
      if (newFiles.length > 0) {
        onFilesChange([...files, ...newFiles]);
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      alert('Kunne ikke laste opp fil(er). Prøv igjen.');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (fileId) => {
    // Remove from local state - actual deletion will happen when user clicks "Lagre dokumenter"
    const updatedFiles = files.filter(f => f.id !== fileId);
    onFilesChange(updatedFiles);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const replaceFile = async (fileId, newFile) => {
    const fileIndex = files.findIndex(f => f.id === fileId);
    if (fileIndex === -1 || !user) return;
    
    const oldFile = files[fileIndex];
    setUploading(true);
    
    try {
      // First, delete the old file from server if it exists
      if (oldFile.uploaded && oldFile.serverUrl) {
        try {
          await userApi.deleteDocument(user.id, oldFile.serverUrl);
          console.log('🗑️ Old file deleted from server');
        } catch (error) {
          console.warn('Could not delete old file:', error);
          // Continue anyway - we'll overwrite
        }
      }
      
      let processedFile = newFile;
      
      // Compress if needed
      if (newFile.type.startsWith('image/')) {
        console.log(`🗜️ Compressing ${newFile.name}...`);
        processedFile = await compressImage(newFile, 0);
        console.log(`✅ Compressed to ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`);
      }
      
      // Upload new file to server
      const response = await userApi.uploadDocument(user.id, processedFile, type);
      console.log('✅ New file uploaded to server');
      
      // Update file object with server URL
      const updatedFile = {
        id: Date.now(),
        name: processedFile.name,
        originalName: newFile.name,
        size: processedFile.size,
        originalSize: newFile.size,
        type: processedFile.type,
        uploadDate: new Date().toISOString(),
        // Check if file_url is already a full URL (e.g., CloudFront) or needs domain prefix
        url: response.file_url.startsWith('http') ? response.file_url : `${window.location.origin}${response.file_url}`,
        serverUrl: response.file_url,
        compressed: processedFile.size < newFile.size,
        uploaded: true
      };
      
      const updatedFiles = [...files];
      updatedFiles[fileIndex] = updatedFile;
      onFilesChange(updatedFiles);
      
      alert('Fil erstattet! Husk å klikke "Lagre dokumenter" for å lagre permanent.');
      
    } catch (error) {
      console.error('Replace error:', error);
      alert(`Kunne ikke erstatte fil: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Function to save display name to backend
  const saveDisplayName = async (file, newName) => {
    if (!user?.id || !file.serverUrl) {
      toast.error('Kunne ikke lagre navn - mangler brukerdata');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file_url', file.serverUrl);
      formData.append('display_name', newName.trim());
      formData.append('document_type', type);

      const response = await fetch(`${API_BASE}/api/users/${user.id}/documents/rename`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Kunne ikke endre navn');
      }

      // Update local file state with new display name
      const updatedFiles = files.map(f => 
        f.id === file.id ? { ...f, displayName: newName.trim() } : f
      );
      onFilesChange(updatedFiles);
      
      // Refresh user data to get updated display names
      if (refreshUser) {
        await refreshUser();
      }
      
      toast.success('Dokumentnavn oppdatert');
      setEditingFileId(null);
      setEditingName('');
    } catch (error) {
      console.error('Rename error:', error);
      toast.error(error.message || 'Kunne ikke endre navn');
    }
  };

  // Removed imageFiles filter since we no longer show thumbnail gallery

  return (
    <div className="p-4 bg-gray-700 rounded-lg">
      <div className="flex items-center mb-3">
        <Upload className="h-5 w-5 text-gray-400 mr-3" />
        <div className="flex-1">
          <p className="text-white font-medium">{title}</p>
          <p className="text-sm text-gray-400">{description}</p>
        </div>
      </div>
      
      {/* Progress indicators */}
      {Object.keys(compressionProgress).length > 0 && (
        <div className="mb-3">
          {Object.entries(compressionProgress).map(([fileIndex, progress]) => (
            <div key={fileIndex} className="mb-2">
              <div className="flex justify-between text-sm text-gray-400 mb-1">
                <span>Komprimerer bilde {parseInt(fileIndex) + 1}...</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-gray-600 rounded-full h-2">
                <div 
                  className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Display uploaded files */}
      {files.length > 0 ? (
        <div className="space-y-3 mb-4">
          
          {/* List view for all files - WITH EDITABLE DISPLAY NAME */}
          <div className="space-y-2">
            {files.map((file, fileIndex) => {
              // Extract simple file type badge (3 letters)
              const getFileTypeBadge = (fileName, fileType) => {
                if (fileType === 'application/pdf') return 'PDF';
                if (fileType?.startsWith('image/jpeg') || fileType?.startsWith('image/jpg')) return 'JPG';
                if (fileType?.startsWith('image/png')) return 'PNG';
                // Fallback: use file extension
                const ext = fileName?.split('.').pop()?.toUpperCase();
                return ext?.substring(0, 3) || 'DOC';
              };
              
              const fileTypeBadge = getFileTypeBadge(file.name, file.type);
              
              // Get display name from props or file object
              const currentDisplayName = file.displayName || (displayNames && displayNames[fileIndex]) || null;
              const isEditing = editingFileId === file.id;
              
              // Extract clean filename (remove UUID prefixes)
              const getCleanFilename = (filename) => {
                if (!filename) return 'Dokument';
                // Remove common UUID patterns like "abc123_" from start
                const parts = filename.split('_');
                if (parts.length > 1 && parts[0].length >= 8) {
                  return parts.slice(1).join('_');
                }
                return filename;
              };
              
              return (
                <div key={file.id} className="p-3 bg-gray-600 rounded">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1 min-w-0">
                      {/* File type badge - rounded square box with 3-letter abbreviation */}
                      <div className="flex-shrink-0 w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center mr-3">
                        <span className="text-xs font-bold text-yellow-500">{fileTypeBadge}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        {isEditing ? (
                          /* Edit mode - inline input */
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              className="flex-1 px-2 py-1 text-sm bg-gray-800 border border-gray-500 rounded text-white focus:outline-none focus:border-yellow-500"
                              placeholder="Skriv inn navn..."
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && editingName.trim()) {
                                  saveDisplayName(file, editingName);
                                } else if (e.key === 'Escape') {
                                  setEditingFileId(null);
                                  setEditingName('');
                                }
                              }}
                            />
                            <button
                              onClick={() => {
                                if (editingName.trim()) {
                                  saveDisplayName(file, editingName);
                                }
                              }}
                              className="p-1 text-green-400 hover:text-green-300"
                              title="Lagre navn"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingFileId(null);
                                setEditingName('');
                              }}
                              className="p-1 text-gray-400 hover:text-gray-300"
                              title="Avbryt"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          /* Display mode */
                          <>
                            <p className="text-sm text-white truncate font-medium">
                              {currentDisplayName || getCleanFilename(file.name)}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {file.type || 'Ukjent filtype'}
                              {currentDisplayName && (
                                <span className="ml-2">({getCleanFilename(file.name)})</span>
                              )}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {!isEditing && (
                      <div className="flex items-center space-x-1 ml-2">
                        {/* Edit name button */}
                        <button
                          onClick={() => {
                            setEditingFileId(file.id);
                            setEditingName(currentDisplayName || getCleanFilename(file.name));
                          }}
                          className="p-1 text-yellow-400 hover:text-yellow-300"
                          title="Endre navn"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        
                        {/* Download button */}
                        <button
                          onClick={() => {
                            // Create download link
                            const link = document.createElement('a');
                            link.href = file.url;
                            link.download = currentDisplayName || file.name || 'dokument';
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          className="p-1 text-green-400 hover:text-green-300"
                          title="Last ned fil"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        
                        {/* Replace button */}
                        <input
                          type="file"
                          id={`replace-${file.id}`}
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              replaceFile(file.id, e.target.files[0]);
                            }
                          }}
                        />
                        <button
                          onClick={() => document.getElementById(`replace-${file.id}`).click()}
                          className="p-1 text-blue-400 hover:text-blue-300"
                          title="Erstatt fil"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                        
                        {/* Delete button */}
                        <button
                          onClick={() => removeFile(file.id)}
                          className="p-1 text-red-400 hover:text-red-300"
                          title="Slett fil"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-400 mb-3">
          Ingen filer lastet opp
        </div>
      )}
      
      {/* Upload button */}
      <input
        type="file"
        id={`${type}-upload`}
        accept=".pdf,.jpg,.jpeg,.png"
        multiple={multiple}
        className="hidden"
        onChange={(e) => handleFileUpload(e.target.files)}
      />
      <button 
        onClick={() => document.getElementById(`${type}-upload`).click()}
        disabled={uploading}
        className="flex items-center px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50"
      >
        <ArrowUp className="h-4 w-4 mr-2" />
        {uploading ? 'Laster opp...' : 'Last opp filer'}
      </button>
      
      {infoLink && (
        <p className="text-xs text-yellow-500 mt-2">
          <a 
            href={infoLink.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:text-yellow-400 inline-flex items-center"
          >
            {infoLink.text} <ExternalLink className="h-3 w-3 ml-1" />
          </a>
        </p>
      )}
      
      {/* Simple Image Preview Modal - REMOVED: no longer shows thumbnail gallery (see line 312) */}
    </div>
  );
};

export default ImageUpload;